<template>
  <form-component>
    <form
      id="account"
      data-vv-scope="account"
      novalidate="novalidate"
      @submit.prevent="onSubmit"
    >
      <div class="form-row">
        <div class="col-12">
          <input-text
            id="officeAddress"
            v-model="officeAddress"
            v-validate="'required'"
            name="officeAddress"
            data-vv-name="officeAddress"
            data-vv-as="office address"
            label="Office Address"
            type="text"
            required
            :error="errors.first('account.officeAddress')"
          />

          <input-text
            id="officeCity"
            v-model="officeCity"
            v-validate="'required'"
            name="officeCity"
            data-vv-name="officeCity"
            data-vv-as="city"
            label="City"
            type="text"
            required
            :error="errors.first('account.officeCity')"
          />

          <div class="form-row">
            <div class="col-12 col-md-6">
              <label class="replacement-label">STATE</label>
              <div class="form-row hide-label">
                <div class="col-12">
                  <state-picker v-model="officeState" />
                </div>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <input-text
                id="officeZip"
                v-model="officeZip"
                v-validate="'required'"
                name="officeZip"
                data-vv-name="officeZip"
                data-vv-as="zip"
                label="Zip Code"
                type="number"
                required
                :error="errors.first('account.officeZip')"
              />
            </div>
          </div>

          <input-text
            id="officePhone"
            v-model="officePhone"
            v-validate="'required'"
            name="officePhone"
            data-vv-name="officePhone"
            data-vv-as="office phone"
            label="Office Phone"
            type="tel"
            required
            :error="errors.first('account.officePhone')"
          />
        </div>
      </div>

      <div class="form-row">
        <div class="col-12">
          <label class="replacement-label">SPECIALTIES</label>
          <specialty-picker
            v-model="specialties"
            data-tags="specialties"
          />
        </div>
      </div>

      <div class="form-row">
        <div class="col-12">
          <label class="replacement-label">ACCEPTED INSURANCE</label>
          <accepted-insurance-picker
            v-model="insurances"
            data-tags="insurances"
          />
        </div>
      </div>

      <button
        type="submit"
        :disabled="saving"
        class="btn btn-primary btn-wide mt-4"
      >
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Save Changes</span>
      </button>
    </form>
  </form-component>
</template>

<script>
import UserClient from '@/api/UserClient';
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';
import { tagFromName } from '@/helpers/tags';
import AcceptedInsurancePicker from '../components/AcceptedInsurancePicker.vue';
import SpecialtyPicker from '../components/SpecialtyPicker.vue';
import StatePicker from '../components/StatePicker.vue';

export default {
  name: 'CounselorAccount',

  components: {
    FormComponent,
    InputText,
    AcceptedInsurancePicker,
    SpecialtyPicker,
    StatePicker,
  },

  data() {
    return {
      userClient: new UserClient(this.$http),
      saving: false,

      officeAddress: '',
      officeCity: '',
      officeState: '',
      officeZip: '',
      officePhone: '',

      specialties: [],
      insurances: [],
    };
  },

  async mounted() {
    const counselor = this.$store.getters.currentUser.counselor_profile;
    if (!counselor) {
      this.$store.commit('setGlobalAlert', {
        msg: 'There was an error loading this page. Please try again!',
        error: true
      });
      console.error('user.counselor_profile was falsy!', this.$store.getters.currentUser);
      return;
    }

    this.insurances = counselor.accepted_insurance_carriers.map(tagFromName);

    if (counselor.office_address) {
      const address = counselor.office_address;
      this.officeAddress = address.street_address || '';
      this.officeCity = address.locality || '';
      this.officeState = address.region || '';
      this.officeZip = address.postal_code || '';
    }

    this.officePhone = (counselor.office_phone || '').replace(/^\+1/, '');
    this.specialties = counselor.specialties.map(tagFromName);
  },

  methods: {
    async onSubmit() {
      const result = await this.$validator.validateAll('account');
      if (!result) {
        this.$store.commit('setGlobalAlert', {
          msg: "Please make sure you've filled out all of the required fields!",
          error: true
        });
        return;
      }

      const officePhone = this.officePhone.replace(/[^\d.]/g, '');
      if (officePhone.length > 0 && officePhone.length !== 10) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Please enter your full office phone number. For example, (585) 123-1234',
          error: true
        });
        return;
      }

      try {
        this.errors.clear();

        this.saving = true;

        const data = {
          accepted_insurance_carrier_ids: this.insurances.map((s) => s.id),
          office_address: {
            street_address: this.officeAddress,
            locality: this.officeCity,
            region: this.officeState,
            postal_code: this.officeZip
          },
          office_phone: `+1${officePhone}`,
          specialty_ids: this.specialties.map((s) => s.id)
        };
        await this.userClient.saveCounselorProfile(data);

        this.$store.commit('setGlobalAlert', {
          msg: 'Profile Updated!'
        });
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to save account. Please try again!',
          error: true
        });
      } finally {
        this.saving = false;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.account {
  .profile-button {
    @media (min-width: $md-min) {
      min-width: 150px;
    }
  }

  label {
    @include label;

    font-size: 12px;
  }

  h2 {
    margin-top: 10px;
    font-size: 28px;
    margin-bottom: 30px;
  }

  .replacement-label {
    text-transform: uppercase;
    font-size: 10px;
    opacity: 0.75;
    letter-spacing: 0.1rem;
    margin-bottom: 5px;
    color: white;
  }

  .upload-btn {
    width: 100%;
    margin-top: 10px;

    i,
    svg {
      margin-right: 7px;
    }
  }

  .right-lg {
    opacity: 0.75;
    position: relative;
    top: -8px;

    label {
      text-transform: none !important;
    }

    @media (min-width: $md-min) {
      text-align: right;
      top: 0;
    }
  }

  .add-file-btn {
    height: 45px;
    width: 100%;
    border-color: $green;
    background: none;
    color: $green;

    &:hover {
      border-color: $green;
      background: $green;
      color: #1c3a58;
    }
  }
}
</style>
